import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  Clear,
  Description,
  LinkOff,
} from '@mui/icons-material';
import {
  FaLink,
  FaFileExcel,
  FaDownload,
} from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL, { apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import InterfaceHeader from '../components/InterfaceHeader';
import { TOOL_COLORS } from '../constants/colors';
import RelatedToolsBar from '../components/RelatedToolsBar';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';

// Reverse-direction tools — for users without an Excel of links yet,
// point them at the two pages that generate one.
const AWS_LINK_GENERATORS = [
  {
    name:    'AWS File Links',
    path:    '/dashboard/aws-file-links',
    accent:  '#2563EB',
    Icon:    CloudUploadRoundedIcon,
    blurb:   "Don't have an Excel of links yet? Upload images here (or paste an Excel of Item IDs) and AWS File Links will push them to S3 and hand you back a spreadsheet of URLs.",
    ctaTag:  'Generate links',
  },
  {
    name:    'AWS File Links Alternate',
    path:    '/dashboard/aws-file-links-alternate',
    accent:  '#059669',
    Icon:    LayersRoundedIcon,
    blurb:   'Rugs- or furniture-specific link generator with shape filtering and size interchange. Useful when AWS File Links is too generic for your inventory.',
    ctaTag:  'Open rugs / furniture flow',
  },
];

const BrokenLinkChecker = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const logEndRef = useRef(null);
  const isDark = theme.palette.mode === 'dark';

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setContextLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [logs, setLogs] = useState([]);

  // Single link check
  const [singleUrl, setSingleUrl] = useState('');
  const [singleChecking, setSingleChecking] = useState(false);
  const [singleResult, setSingleResult] = useState(null);

  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  const handleSingleCheck = async () => {
    if (!singleUrl.trim()) return;
    setSingleChecking(true);
    setSingleResult(null);
    try {
      const response = await apiCall(`${API_BASE_URL}/api/BrokenLinkChecker/check-single?url=${encodeURIComponent(singleUrl.trim())}`);
      setSingleResult(response);
    } catch (err) {
      setSingleResult({ status: 0, statusText: 'Error', responseTime: 0, error: err.message });
    } finally {
      setSingleChecking(false);
    }
  };

  // Auto-scroll logs
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const fetchStatus = async (processId) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/api/BrokenLinkChecker/status/${processId}`);

      if (response.logs && response.logs.length > 0) {
        setLogs(response.logs);
        setContextLogs(processId, response.logs);
      }

      if (response.progressPercentage !== undefined) {
        setProgress(response.progressPercentage);
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(processId, response.fileName, response.awsUrl);
        }

        setResults({
          success: true,
          fileCount: response.processedItems || 0,
          fileName: response.fileName,
        });
        completeActivityProcess(processId, 'success', 'Link checking completed successfully');
        setSuccess('Processing complete! Excel file with link statuses has been generated.');
        setLoading(false);
        setProgress(null);
      } else if (response.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }
        setError(`Error: ${response.message}`);
        setLoading(false);
        setProgress(null);
        completeActivityProcess(processId, 'error', response.message);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, serverFileName, awsUrl) => {
    try {
      addLog(processId, 'Downloading file...');

      // Primary: download from S3 URL (survives app pool recycles)
      if (awsUrl) {
        const link = document.createElement('a');
        link.href = awsUrl;
        link.download = serverFileName || `Broken Link Checker Result ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Also open in new tab
        window.open(awsUrl, '_blank');

        addLog(processId, 'Download complete!');
        return;
      }

      // Fallback: download from in-memory endpoint
      const response = await fetch(`${API_BASE_URL}/api/BrokenLinkChecker/download/${processId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || `Broken Link Checker Result ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Also open in new tab
      window.open(url, '_blank');

      window.URL.revokeObjectURL(url);

      addLog(processId, 'Download complete!');
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, `Download error: ${err.message}`);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/BrokenLinkChecker/sample`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download sample file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      link.download = `Broken Link Checker Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download sample file.');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const droppedFile = droppedFiles[0];
    if (droppedFile && droppedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop an Excel or CSV file only (.xlsx, .xls, .csv)');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
        setError('Please select an Excel or CSV file only.');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleProcess = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    setLogs([]);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiCall(`${API_BASE_URL}/api/BrokenLinkChecker/process`, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addActivityProcess(serverProcessId, 'Processing Broken Link Checker', 'Broken Link Checker');
        addLog(serverProcessId, 'Starting link checking...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred during processing. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(null);
    setLogs([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <InterfaceHeader
          title="BROKEN LINK CHECKER"
          color={TOOL_COLORS.LINK.primary}
          category="links"
        />

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Upload & Process
              </Typography>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                Check a single URL or upload any Excel/CSV file. If a "Link" column exists, it uses that. Otherwise, it auto-detects all URLs across every cell.
              </Typography>

              {/* Single Link Check */}
              <Card sx={{ mb: 3, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(59,130,246,0.03)', border: `1px solid ${alpha(TOOL_COLORS.LINK.primary, 0.15)}` }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.text.primary }}>
                    Quick Check — Single URL
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://example.com/page"
                      value={singleUrl}
                      onChange={(e) => { setSingleUrl(e.target.value); setSingleResult(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && singleUrl.trim()) handleSingleCheck(); }}
                      disabled={singleChecking}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSingleCheck}
                      disabled={!singleUrl.trim() || singleChecking}
                      sx={{ minWidth: 90, bgcolor: TOOL_COLORS.LINK.primary, '&:hover': { bgcolor: alpha(TOOL_COLORS.LINK.primary, 0.85) }, textTransform: 'none', fontWeight: 600 }}
                    >
                      {singleChecking ? <CircularProgress size={20} color="inherit" /> : 'Check'}
                    </Button>
                  </Box>
                  {singleResult && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Chip
                        size="small"
                        label={singleResult.status || 'Error'}
                        sx={{
                          fontWeight: 700,
                          bgcolor: singleResult.status >= 200 && singleResult.status < 300 ? '#4CAF50'
                            : singleResult.status >= 300 && singleResult.status < 400 ? '#FF9800'
                            : '#f44336',
                          color: '#fff',
                        }}
                      />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {singleResult.statusText}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                        {singleResult.responseTime}ms
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Divider sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>OR bulk check from file</Typography>
              </Divider>

              {/* File Upload Area */}
              <Card sx={{ mb: 3, bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.LINK.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Excel / CSV File
                    </Typography>
                    {file && (
                      <Tooltip title="1 file selected">
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                  />

                  {/* Drag & Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.LINK.primary : file ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.LINK.light
                        : file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: file ? '#4CAF50' : TOOL_COLORS.LINK.primary,
                        bgcolor: file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.LINK.light,
                      }
                    }}
                  >
                    {!file ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.LINK.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging
                            ? 'Drop file here'
                            : 'Drop your Excel or CSV file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) or CSV (.csv) files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop to replace
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File Info */}
                  {file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                          borderColor: TOOL_COLORS.LINK.primary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.LINK.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({formatFileSize(file.size)})
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcess}
                  disabled={!file || loading}
                  sx={{
                    bgcolor: TOOL_COLORS.LINK.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                    px: 4
                  }}
                  startIcon={<FaLink />}
                >
                  {loading ? 'Processing...' : 'Process'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleDownloadSample}
                  disabled={loading}
                  sx={{
                    borderColor: TOOL_COLORS.LINK.primary,
                    color: TOOL_COLORS.LINK.primary,
                    '&:hover': {
                      borderColor: TOOL_COLORS.LINK.hover,
                      bgcolor: TOOL_COLORS.LINK.light
                    }
                  }}
                  startIcon={<FaDownload />}
                >
                  Download Template
                </Button>

                {(file || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              {/* Progress */}
              {loading && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Checking links...
                  </Typography>
                  <LinearProgress
                    variant={progress > 0 ? "determinate" : "indeterminate"}
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: TOOL_COLORS.LINK.primary,
                        borderRadius: 4,
                      }
                    }}
                  />
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {/* Activity log removed - results show in Status & Downloads */}
            </Paper>
          </Grid>

          {/* Status & Info Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaLink color={TOOL_COLORS.LINK.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.fileCount || 0} Links Checked`}
                        secondary="Each URL was tested for HTTP status"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaFileExcel color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Excel File Generated"
                        secondary="Contains Link, Status, StatusText, ResponseTime"
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Excel file has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Download the template file<br />
                2. Add your URLs in the "Link" column<br />
                3. Upload the file<br />
                4. Click "Process" to check all links<br />
                5. Results will auto-download with status for each URL
              </Typography>
            </Paper>

            {/* "Next steps" — points users at the tools that generate
                the Excel of links they'd feed into this checker. */}
            <RelatedToolsBar tools={AWS_LINK_GENERATORS} title="Need to generate links first?" subtitle="Generate an Excel of image links" layout="stack" />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BrokenLinkChecker;
